import React, { useMemo, useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import {
  useAcademicPrograms,
  useCreateAcademicProgram
} from "../../../../hooks/api/education/useAcademicStructure";
import { formatEntityStatus, isArchived } from "../common/academicUtils";

const ProgramList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { data: programs = [], isPending, refetch } = useAcademicPrograms();
  const createProgram = useCreateAcademicProgram();
  const [name, setName] = useState("");

  const sortedPrograms = useMemo(
    () => [...programs].sort((a, b) => a.name.localeCompare(b.name)),
    [programs]
  );

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-programs", "Loading programs...")}
      />
    );
  }

  return (
    <Box data-testid="program-list-page" sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2
        }}
      >
        <Typography variant="h5" component="h1">
          {t("programs-title", "Programs")}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          label={t("program-name-label", "Program name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <AppButton
          disabled={!name.trim() || createProgram.isPending}
          onClick={async () => {
            await createProgram.mutateAsync({ name: name.trim() });
            setName("");
            refetch();
          }}
        >
          {t("add-program", "Add program")}
        </AppButton>
      </Stack>

      {sortedPrograms.length === 0 ? (
        <Typography color="text.secondary">
          {t("programs-empty", "No programs yet.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {sortedPrograms.map((program) => (
            <Box
              component="li"
              key={program.id}
              sx={{
                py: 1.5,
                px: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                opacity: isArchived(program.status) ? 0.65 : 1
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {program.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatEntityStatus(program.status)}
                  {program.code ? ` · ${program.code}` : ""}
                </Typography>
              </Box>
              <AppButton variant="text" onClick={() => navigate(program.id)}>
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProgramList;
